import { useEffect, useState } from "react";
import useData from "./useData";

const useSkuListing = () => {
  const { getFromServer, executeAction } = useData();

  const [client, setClient] = useState(null);
  const [listingData, setListingData] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadClient = async (clientId) => {
    const result = await getFromServer({
      entityName: "Client",
      id: clientId,
    });
    setClient(result);
    return result;
  };

  const fetchListings = async (clientId, skuId, filters = {}) => {
    setLoading(true);
    try {
      let currentClient = client;
      if (!currentClient || currentClient.id !== clientId) {
        currentClient = await loadClient(clientId);
      }

      if (!currentClient?.listingOptions) {
        setListingData([]);
        setLoading(false);
        return;
      }

      const action = getActionName(currentClient.listingOptions.retailLevel);

      const data = {
        clientId,
        ...filters,
      };

      const results = await executeAction({
        entityName: "SKUListing",
        action,
        data,
      });

      const arr =
        results?.isSuccessful && Array.isArray(results.data)
          ? results.data
          : [];

      // Filter by skuId client-side
      const filtered = arr.filter((l) => String(l.skuId) === String(skuId));

      setListingData(filtered);
    } catch (err) {
      console.error("Error fetching listings:", err);
      setListingData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchListingsForLocation = async (clientId, locationId) => {
    setLoading(true);

    try {
      let currentClient = client;
      if (!currentClient || currentClient.id !== clientId) {
        currentClient = await loadClient(clientId);
      }

      if (!currentClient?.listingOptions || !locationId) {
        setListingData([]);
        return [];
      }

      const location = await getFromServer({
        entityName: "Location",
        id: locationId,
      });

      if (!location) {
        setListingData([]);
        return [];
      }

      const action = getActionName(currentClient.listingOptions.retailLevel);

      const data = {
        clientId,
      };

      if (action === "getLocationListings") {
        data.locationId = locationId;
      } else if (action === "getBannerListings") {
        data.bannerId = location.bannerId;
      } else if (action === "getRetailerListings") {
        let retailerId = location.retailerId;

        if (!retailerId && location.bannerId) {
          const banner = await getFromServer({
            entityName: "Banner",
            id: location.bannerId,
          });
          retailerId = banner?.retailerId;
        }

        if (retailerId) {
          data.retailerId = retailerId;
        }
      }

      const results = await executeAction({
        entityName: "SKUListing",
        action,
        data,
      });

      const arr =
        results?.isSuccessful && Array.isArray(results.data)
          ? results.data
          : [];

      // Keep only records that apply to the selected location when a location scope exists.
      const filtered = arr.filter((item) => {
        if (!item.locationId) return true;
        return String(item.locationId) === String(locationId);
      });

      setListingData(filtered);
      return filtered;
    } catch (err) {
      console.error("Error fetching location listings:", err);
      setListingData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getActionName = (retailLevel) => {
    switch (retailLevel) {
      case "retailer":
        return "getRetailerListings";
      case "banner":
        return "getBannerListings";
      case "location":
      case "account":
        return "getLocationListings";
      default:
        return "getNationalListings";
    }
  };

  return {
    client,
    listingData,
    loading,
    fetchListings,
    fetchListingsForLocation,
  };
};

export default useSkuListing;
